import { CheckCircle2, FileText, Trash2, Upload } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { etatGabarit, libelleAgregateur } from '@/shared/utils/agregateurMandat';

/**
 * LA COLONNE DES MODÈLES — extraite telle quelle de `MandatTemplatePage`.
 *
 * ⚠️ ELLE PORTE `gabarits-liste`, ET CE TESTID NE DOIT PAS DISPARAÎTRE : c'est celui que
 * `~/uat-flexcore/uat-gabarits.mjs` interroge. Le rail a rétréci de 240 à 144 px pour
 * rendre le plan plus grand ; il n'a pas changé de rôle.
 */
export default function ListeDesGabarits({
  modeles, selection, systeme, survol, setSurvol, envoi,
  onChoisir, onActiver, onSupprimer, onImporter, onCliquerImporter, fmtDate, fmtTaille,
}) {
  return (
    <div className="space-y-1.5 order-3 lg:order-1 overflow-y-auto pr-0.5"
         data-testid="gabarits-liste">
      {modeles.map((m) => (
        <button
          type="button" key={m.id}
          onClick={() => onChoisir(m.id)}
          className={`w-full text-left rounded-lg border p-2 transition-colors ${
            selection === m.id
              ? 'border-marque bg-marque/5'
              : 'border-neutral-200 bg-white hover:bg-neutral-50'}`}
          data-testid={`gabarit-vignette-${m.id}`}
        >
          <div className="flex items-start gap-1.5">
            <FileText className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
              m.active ? 'text-emerald-600' : 'text-neutral-400'}`} />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium text-neutral-900 truncate"
                 title={m.nom_fichier}>{m.nom_fichier}</p>
              <p className="text-[9.5px] text-neutral-500">
                {fmtDate(m.created_at)} · {m.pages} p · {fmtTaille(m.taille)}
              </p>
              {(m.agregateur || m.brouillon) && (
                <p className="text-[9.5px] text-neutral-600" data-testid={`gabarit-portee-${m.id}`}>
                  {libelleAgregateur(m.agregateur) || 'Générique'}
                  {etatGabarit(m) === 'brouillon' && <span className="text-amber-700"> · brouillon</span>}
                </p>
              )}
              {m.active ? (
                <Badge variant="outline"
                       className="mt-1 bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] px-1">
                  <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> Actif
                </Badge>
              ) : (
                <div className="mt-1 flex items-center gap-1">
                  {/* Un brouillon ne s'active pas (C8a) : le serveur le refuserait. */}
                  {etatGabarit(m) !== 'brouillon' && (
                    <span role="button" tabIndex={0}
                          className="text-[9.5px] text-marque underline cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); onActiver(m.id); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onActiver(m.id); } }}
                          data-testid={`gabarit-activer-${m.id}`}>Activer</span>
                  )}
                  <span role="button" tabIndex={0}
                        className="text-neutral-400 hover:text-red-600 cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); onSupprimer(m.id); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onSupprimer(m.id); } }}
                        data-testid={`gabarit-supprimer-${m.id}`}>
                    <Trash2 className="w-2.5 h-2.5" />
                  </span>
                </div>
              )}
            </div>
          </div>
        </button>
      ))}

      {/* Le glisser-déposer d'un FICHIER, lui, reste en HTML5 `dragover/drop` : c'est
          exactement ce pour quoi l'API a été faite — transférer une donnée. Le
          glisser-déposer des ZONES, qui positionne, passe par Pointer Events. */}
      <div
        onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
        onDragLeave={() => setSurvol(false)}
        onDrop={(e) => { e.preventDefault(); setSurvol(false);
                         const f = e.dataTransfer.files?.[0]; if (f) onImporter(f); }}
        className={`rounded-lg border-2 border-dashed p-2.5 text-center ${
          survol ? 'border-marque bg-marque/5' : 'border-neutral-200'}`}
        data-testid="gabarit-depot"
      >
        <Upload className="w-3.5 h-3.5 mx-auto text-neutral-400" />
        <p className="text-[9.5px] text-neutral-500 mt-0.5 leading-tight">
          Déposer un PDF<br />(max 10 Mo)
        </p>
        {/* ⚠️ LE MÊME GESTE, DEUX FAÇONS DE LE FAIRE, AU MÊME ENDROIT.
            Glisser un fichier marche déjà ; encore faut-il savoir qu'on peut, et pouvoir
            faire autrement quand le fichier n'est pas sous la main. Le bouton était dans
            l'en-tête, à l'autre bout de l'écran : deux commandes pour un geste. */}
        <button
          type="button" disabled={envoi}
          onClick={(e) => { e.stopPropagation(); onCliquerImporter(); }}
          className="mt-1.5 w-full text-[10px] text-marque hover:underline disabled:opacity-50"
          data-testid="gabarit-importer"
        >
          {envoi ? 'Import…' : 'ou choisir un fichier'}
        </button>
      </div>

      {/* LE MODÈLE SYSTÈME, ENFIN VISIBLE. C'est le document qui part quand aucun modèle
          n'est actif, et celui sur lequel on retombe quand un gabarit se révèle mauvais.
          Un filet qu'on n'a jamais regardé n'est pas un filet. */}
      <button
        type="button" onClick={() => onChoisir(systeme)}
        className={`w-full text-left rounded-lg border p-2 transition-colors ${
          selection === systeme
            ? 'border-marque bg-marque/5'
            : 'border-neutral-200 bg-white hover:bg-neutral-50'}`}
        data-testid="gabarit-vignette-systeme"
      >
        <div className="flex items-start gap-1.5">
          <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-neutral-400" />
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-neutral-900">Modèle système</p>
            <p className="text-[9.5px] text-neutral-500 leading-tight">
              Généré par le code
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}
